import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    useAddCalendarMeetingMutation,
    useEditCalendarMeetingMutation,
    useGetCalendarMeetingQuery,
    useLazyGetCalendarLoadQuery
} from '../../api/calendar.js';
import BaseButton from '../../components/form/BaseButton.jsx';
import BaseInput from '../../components/form/BaseInput.jsx';
import BaseInputCalendar from '../../components/form/BaseInputCalendar.jsx';
import BaseSwitch from '../../components/form/BaseSwitch.jsx';
import AuthLayout from '../../components/layout/AuthLayout.jsx';
import BaseCalendar from '../../components/misc/BaseCalendar.jsx';
import MeetingParticipants from '../../components/misc/MeetingParticipants.jsx';
import Notes from '../../components/misc/Notes.jsx';
import { useErrors } from '../../hooks/useErrors.js';
import { CALENDAR_SCHEMA } from '../../utils/calendar.js';
import { convertDateAndTimeToUTCDateTime, fromUtcDateTime } from '../../utils/helper.js';
import { useSelector } from 'react-redux';
import SharedModelListItem from '../../components/misc/SharedModelListItem.jsx';
import PrivateUsersListItem from '../../components/misc/PrivateUsersListItem.jsx';
import MeetingDeleteConfirmation from '../../components/misc/MeetingDeleteConfirmation.jsx';
import {
    PERMISSION_CALENDAR,
    PERMISSION_CONTACTS,
    PERMISSION_FILES,
    PERMISSION_OWNED_ORGANIZATIONS,
    PERMISSION_PARTNER_ORGANIZATIONS,
    PERMISSION_PROJECTS
} from '../../utils/constants.js';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import { Accordion, AccordionTab } from 'primereact/accordion';
import BaseTextarea from '../../components/form/BaseTextarea.jsx';
import RecurringMeeting from '../../components/misc/RecurringMeeting.jsx';
import IconCloseFull from '../../components/images/IconCloseFull.jsx';
import { Dialog } from 'primereact/dialog';
import { useConditionalGoogleLogin } from '../../hooks/googleLogin.js';
import AssociateProjects from '../../components/misc/AssociateProjects.jsx';
import AssociateOrganizations from '../../components/misc/AssociateOrganizations.jsx';
import AssociateFiles from '../../components/misc/AssociateFiles.jsx';
import { canUpdateOrDelete } from '../../utils/user.js';
import { useGetCountriesQuery } from '../../api/misc.js';
import BaseDropdown from '../../components/form/BaseDropdown.jsx';

const AddEditMeeting = ({ edit = false }) => {
    const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;

    let calendarMeeting = null;
    const { permissions: userPermissions } = useCurrentUser();
    const [deleteVisible, setDeleteVisible] = useState(false);
    const [saveRecurringVisible, setSaveRecurringVisible] = useState(false);
    const user = useSelector((state) => state.user.user);
    const [permittedOrganizations, setPermittedOrganizations] = useState([]);
    const { id } = useParams();

    const [queryString] = useSearchParams();
    const newDate = queryString.get('date');
    const followUpParam = queryString.get('follow_up');
    const roleParam = queryString.get('role');
    const firstNameParam = queryString.get('first_name');
    const lastNameParam = queryString.get('last_name');
    const emailParam = queryString.get('email');
    const contactIdParam = queryString.get('contact_id');

    const [showParticipantSearch, setShowParticipantSearch] = useState(true);
    const [showProjectSearch, setShowProjectSearch] = useState(true);
    const [showOrganizationSearch, setShowOrganizationSearch] = useState(true);
    const [isPrivate, setIsPrivate] = useState(true);
    const [showFileSearch, setShowFileSearch] = useState(true);

    if (edit && id) {
        const { data: meetingResponse } = useGetCalendarMeetingQuery(id);

        calendarMeeting = useMemo(() => {
            if (meetingResponse?.response?.meeting) {
                return meetingResponse.response.meeting;
            }

            return null;
        }, [meetingResponse]);
    }

    useEffect(() => {
        if (!user?.role?.permissions || !user?.organizations) return;

        user.organizations.forEach((item) => {
            const permission = user.role.permissions.find(
                (value) => value.organization_id === item.id && value.permission === PERMISSION_CALENDAR
            );

            const hasPermission = permission && ((permission.create && !edit) || (permission.update && edit));

            if (hasPermission) {
                setPermittedOrganizations((prevState) => {
                    if (!prevState.some((org) => org.id === item.id)) {
                        return [...prevState, item];
                    }
                    return prevState;
                });
            }
        });
    }, [user, edit]);

    const [addCalendarMeeting, { isLoading, error }] = useAddCalendarMeetingMutation();
    const [editCalendarMeeting, { isLoading: isLoadingEdit, error: errorEdit }] = useEditCalendarMeetingMutation();
    const navigate = useNavigate();

    const { handleSubmit, getValues, formState, control, clearErrors, reset, watch, setValue } = useForm({
        resolver: yupResolver(CALENDAR_SCHEMA),
        defaultValues: {
            title: '',
            location: '',
            country_id: '',
            description: '',
            all_day: false,
            start_date: newDate ? new Date(newDate) : new Date(),
            end_date: newDate ? new Date(newDate) : new Date(),
            start_time: null,
            end_time: null,
            notes: [
                {
                    note: ''
                }
            ],
            participants:
                contactIdParam && roleParam && firstNameParam && lastNameParam
                    ? [
                          {
                              contact_id: contactIdParam,
                              first_name: firstNameParam,
                              last_name: lastNameParam,
                              email: emailParam,
                              role: roleParam
                          }
                      ]
                    : [],
            shared: false,
            shared_organizations: [],
            shared_users: [],
            organizations: [],
            projects: [],
            files: [],
            is_recurrent: false,
            frequency: 'daily',
            week_days: [],
            month_days: [],
            recurrence_end_type: 'never',
            recurrence_end_date: new Date(),
            save_to_all: false,
            google_access_code: null
        },
        reValidateMode: 'onSubmit'
    });

    const [selectedDates, setSelectedDates] = useState([
        newDate ? new Date(newDate) : new Date(),
        newDate ? new Date(newDate) : new Date()
    ]);

    const allowMeetApi = useConditionalGoogleLogin(googleClientId, {
        flow: 'auth-code',
        scope: 'openid https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/calendar',
        access_type: 'offline',
        redirectUri: import.meta.env.VITE_APP_URL,
        onSuccess: async (tokenResponse) => {
            setValue('google_access_code', tokenResponse.code);
        },
        onError: (errorResponse) => {
            setValue('google_access_code', null);
            console.error('Google flow authorize failed', errorResponse);
        }
    });

    const cancelGoogleMeet = () => {
        setValue('google_access_code', null);
    };

    const {
        fields: fieldsParticipants,
        append: appendParticipant,
        remove: removeParticipant
    } = useFieldArray({
        control,
        name: 'participants'
    });

    const {
        fields: fieldsProjects,
        append: appendProject,
        remove: removeProject
    } = useFieldArray({
        control,
        name: 'projects'
    });

    const {
        fields: fieldsOrganizations,
        append: appendOrganization,
        remove: removeOrganization
    } = useFieldArray({
        control,
        name: 'organizations'
    });

    const {
        fields: fieldsFiles,
        append: appendFile,
        remove: removeFile
    } = useFieldArray({
        control,
        name: 'files'
    });

    const {
        fields: fieldsNotes,
        append: appendNote,
        remove: removeNote
    } = useFieldArray({
        control,
        name: 'notes'
    });

    const { errors } = edit ? useErrors(errorEdit?.message, formState) : useErrors(error?.message, formState);
    const sharedOrganizations = watch('shared_organizations');
    const sharedUsers = watch('shared_users');

    const handleSharedOrganizations = (index, value) => {
        if (permittedOrganizations.length > 0) {
            const organizationId = permittedOrganizations[index].id;

            const updatedSharedOrganizations = value
                ? [...sharedOrganizations, organizationId]
                : sharedOrganizations.filter((orgId) => orgId !== organizationId);

            setValue('shared_organizations', updatedSharedOrganizations);

            if (updatedSharedOrganizations.length === 0) {
                handleSharedUsers(true);
            }
        }
    };

    const handleSharedUsers = (value) => {
        setIsPrivate(value);
        if (value) {
            setValue('shared_organizations', []);
        } else {
            setValue('shared_users', []);
            if (permittedOrganizations.length > 0) {
                handleSharedOrganizations(0, true);
            }
        }
    };

    const handleUserMark = (id, value) => {
        const updatedSharedUsers = value ? [...sharedUsers, id] : sharedUsers.filter((id) => id !== id);

        setValue('shared_users', updatedSharedUsers);
    };

    const renderShared = () => {
        if (permittedOrganizations.length === 0) {
            return <div className='text-theme1-text1 text-lg'>No organizations found.</div>;
        }

        return (
            <div>
                {permittedOrganizations.map(({ id, name }, index) => (
                    <SharedModelListItem
                        key={id}
                        index={index}
                        text={name}
                        isMarked={sharedOrganizations.includes(id)}
                        onChangeSwitch={handleSharedOrganizations}
                        isLoading={isLoading}
                        disabled={isPrivate}
                    />
                ))}

                <PrivateUsersListItem
                    key='separate-item'
                    text={'Private'}
                    isMarked={isPrivate}
                    onChangeSwitch={handleSharedUsers}
                    onChangeMark={handleUserMark}
                    modelSharedUsers={calendarMeeting?.shared_users || []}
                    isLoading={isLoading}
                    permission={PERMISSION_CALENDAR}
                />
            </div>
        );
    };

    const handleDeleteConfirmation = () => {
        setDeleteVisible(true);
    };

    const handleSaveRecurring = () => {
        setSaveRecurringVisible(true);
    };

    const sendEditMeeting = async (requestData) => {
        const finalFiles =
            requestData.files &&
            requestData.files?.map((f) => {
                if (f.file_id) {
                    return {
                        file_id: f.file_id,
                        name: '',
                        path: '',
                        extension: '',
                        type: ''
                    };
                } else {
                    return {
                        file_id: '',
                        name: f.name,
                        path: f.path,
                        extension: f.extension,
                        type: f.type
                    };
                }
            });

        const updatedData = {
            ...requestData,
            start_date: requestData.start_date
                ? convertDateAndTimeToUTCDateTime(requestData.start_date).toISOString()
                : null,
            end_date: requestData.end_date ? convertDateAndTimeToUTCDateTime(requestData.end_date).toISOString() : null,
            start_time:
                requestData.start_date && requestData.start_time
                    ? convertDateAndTimeToUTCDateTime(requestData.start_date, requestData.start_time).toISOString()
                    : null,
            end_time:
                requestData.end_date && requestData.end_time
                    ? convertDateAndTimeToUTCDateTime(requestData.end_date, requestData.end_time).toISOString()
                    : null,
            shared: requestData.shared_organizations.length > 0,
            recurrence_end_date: requestData.recurrence_end_date
                ? convertDateAndTimeToUTCDateTime(requestData.recurrence_end_date).toISOString()
                : null,
            files: finalFiles
        };

        await editCalendarMeeting({ id: id || '', data: updatedData }).then((apiResponse) => {
            if (apiResponse.data?.success) {
                navigate('/calendar/view/' + id);
            } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        });
    };

    const saveToAllOccurrences = () => {
        setValue('save_to_all', true);
        handleSubmit(sendEditMeeting)();
    };

    const sendAddMeeting = async (requestData) => {
        const finalFiles = requestData.files?.map((f) => {
            if (f.file_id) {
                return {
                    file_id: f.file_id,
                    name: '',
                    path: '',
                    extension: '',
                    type: ''
                };
            } else {
                return {
                    file_id: '',
                    name: f.name,
                    path: f.path,
                    extension: f.extension,
                    type: f.type
                };
            }
        });

        const updatedData = {
            ...requestData,
            start_date: requestData.start_date
                ? convertDateAndTimeToUTCDateTime(requestData.start_date).toISOString()
                : null,
            end_date: requestData.end_date ? convertDateAndTimeToUTCDateTime(requestData.end_date).toISOString() : null,
            start_time:
                requestData.start_date && requestData.start_time
                    ? convertDateAndTimeToUTCDateTime(requestData.start_date, requestData.start_time).toISOString()
                    : null,
            end_time:
                requestData.end_date && requestData.end_time
                    ? convertDateAndTimeToUTCDateTime(requestData.end_date, requestData.end_time).toISOString()
                    : null,
            follow_up: !!followUpParam,
            shared: requestData.shared_organizations.length > 0,
            recurrence_end_date: requestData.recurrence_end_date
                ? convertDateAndTimeToUTCDateTime(requestData.recurrence_end_date).toISOString()
                : null,
            files: finalFiles
        };

        await addCalendarMeeting(updatedData).then((apiResponse) => {
            if (apiResponse.data?.success) {
                navigate('/calendar/view/' + apiResponse.data?.response.id);
            } else {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        });
    };

    const formValues = getValues();
    const allDay = watch('all_day');

    const changeDates = (value) => {
        setSelectedDates(value);

        setValue('start_date', value[0] ? new Date(value[0]) : null);
        setValue('end_date', value[1] ? new Date(value[1]) : value[0] ? new Date(value[0]) : null);

        if (value[0] && dayjs(value[0]).format('YYYY-MM-DD') === dayjs(value[1] ?? value[0]).format('YYYY-MM-DD')) {
            setValue('end_time', formValues.start_time);
        }
    };

    const checkAndModifyEndTime = (newStartTime) => {
        if (
            dayjs(formValues.start_date).format('YYYY-MM-DD') === dayjs(formValues.end_date).format('YYYY-MM-DD') &&
            (!formValues.end_time || dayjs(newStartTime) > dayjs(formValues.end_time))
        ) {
            setValue('end_time', newStartTime);
        }
    };

    const startTime = watch('start_time');

    const { data: loadData } = useLazyGetCalendarLoadQuery({});

    const meetingsLoadData = useMemo(() => {
        if (loadData?.response?.dates) {
            return loadData.response.dates;
        }

        return [];
    }, [loadData]);

    const { data: countriesData } = useGetCountriesQuery({});
    const countries = useMemo(() => {
        if (countriesData?.response?.countries) {
            return countriesData.response.countries.map((country) => ({ label: country.name, value: country.id }));
        }

        return [];
    }, [countriesData]);

    const googleAccessCode = watch('google_access_code');

    useEffect(() => {
        if (calendarMeeting) {
            if (!canUpdateOrDelete(user, calendarMeeting, PERMISSION_CALENDAR, 'update')) {
                navigate('/unauthorized');
                return;
            }
            const formattedMeetingData = {
                title: calendarMeeting.title || '',
                location: calendarMeeting.location || '',
                country_id: calendarMeeting.country_id || '',
                all_day: calendarMeeting.all_day || false,
                start_date: calendarMeeting.start_date ? fromUtcDateTime(calendarMeeting.start_date) : new Date(),
                end_date: calendarMeeting.end_date ? fromUtcDateTime(calendarMeeting.end_date) : new Date(),
                start_time: calendarMeeting.start_time ? fromUtcDateTime(calendarMeeting.start_time) : null,
                end_time: calendarMeeting.end_time ? fromUtcDateTime(calendarMeeting.end_time) : null,
                description: calendarMeeting.description || '',
                notes:
                    calendarMeeting.notes && calendarMeeting.notes.length > 0
                        ? calendarMeeting.notes.map((note) => ({
                              note: note.note || ''
                          }))
                        : [{ note: '' }],
                participants:
                    calendarMeeting.participants && calendarMeeting.participants.length > 0
                        ? calendarMeeting.participants.map((participant) => ({
                              contact_id: participant.contact_id || '',
                              first_name: participant.contact.first_name || '',
                              last_name: participant.contact.last_name || '',
                              email: participant.email || '',
                              role: participant.role || '',
                              avatar: participant.contact.avatar || ''
                          }))
                        : [],
                organizations:
                    calendarMeeting.organizations &&
                    calendarMeeting.organizations.map((org) => ({
                        organization_id: org.organization_id,
                        name: org.name
                    })),
                projects:
                    calendarMeeting.projects &&
                    calendarMeeting.projects.map((project) => ({
                        project_id: project.project_id,
                        name: project.name
                    })),
                files: calendarMeeting.files?.map((file) => ({
                    file_id: file.file_id,
                    path: file.url,
                    name: file.title,
                    type: file.category,
                    extension: file.extension
                })),
                shared: calendarMeeting.shared,
                shared_organizations: calendarMeeting.shared_organizations,
                shared_users: calendarMeeting.shared_users.map((shareUser) => shareUser.id),
                is_recurrent: calendarMeeting.is_recurrent || false,
                frequency: calendarMeeting.frequency || 'daily',
                week_days: calendarMeeting.week_days || [],
                month_days: calendarMeeting.month_days || [],
                recurrence_end_type: calendarMeeting.recurrence_end_type || 'never',
                recurrence_end_date: calendarMeeting.recurrence_end_date
                    ? new Date(dayjs(calendarMeeting.recurrence_end_date).format('YYYY-MM-DD'))
                    : new Date()
            };

            reset(formattedMeetingData);

            setSelectedDates([formattedMeetingData.start_date, formattedMeetingData.end_date]);
            setIsPrivate(!calendarMeeting.shared);
        }
    }, [calendarMeeting, navigate, reset, user]);

    useEffect(() => {
        if (roleParam && firstNameParam && lastNameParam) {
            setShowParticipantSearch(true);
        }
    }, [roleParam, firstNameParam, lastNameParam]);

    return (
        <AuthLayout title='Calendar'>
            <div className='flex flex-col max-w-3xl w-full mx-auto px-8'>
                <div className='my-5 flex items-center justify-between'>
                    <div className='text-theme1-title2 font-bold text-2xl'>{edit ? 'Edit Meeting' : 'Add Meeting'}</div>
                </div>
                <BaseCalendar
                    value={selectedDates}
                    setDate={changeDates}
                    selectionMode='range'
                    minDate={new Date()}
                    loadData={meetingsLoadData}
                    disableDates={edit && !calendarMeeting?.is_original && calendarMeeting?.is_recurrent}
                />
                <Accordion className='custom-accordion mt-2' multiple={false} activeIndex={edit ? null : 0}>
                    <AccordionTab
                        key='shared-within'
                        header={
                            <span className='text-lg font-bold text-theme1-text2'>
                                Shared within {!edit && <span className='text-theme1-brightRed'>*</span>}
                            </span>
                        }
                    >
                        {renderShared()}
                    </AccordionTab>
                </Accordion>

                <div className='mb-10'>
                    <BaseInput
                        className='mt-2'
                        type='text'
                        control={control}
                        placeholder='Title'
                        name='title'
                        clearErrors={clearErrors}
                        errors={[...(errors?.title ? errors.title : [])]}
                    />
                    {(!googleClientId || !googleAccessCode) && (
                        <BaseInput
                            className='mt-4'
                            type='text'
                            control={control}
                            placeholder='Location or online meet link'
                            name='location'
                            disabled={!!calendarMeeting?.google_event_id}
                            clearErrors={clearErrors}
                            errors={[...(errors?.location ? errors.location : [])]}
                        />
                    )}
                    {googleClientId &&
                        !calendarMeeting?.google_event_id &&
                        (!googleAccessCode ? (
                            <BaseButton className='btn-red-outline mt-4' onClick={() => allowMeetApi()}>
                                Use google to generate meet link
                            </BaseButton>
                        ) : (
                            <div className='grid grid-cols-6 gap-4 mt-4'>
                                <div className='col-span-4 text-theme1-text1 font-semibold'>
                                    Link for meet will be generated upon save
                                </div>
                                <div className='col-span-2'>
                                    <BaseButton onClick={() => cancelGoogleMeet()} className='btn-primary-outline'>
                                        Cancel
                                    </BaseButton>
                                </div>
                            </div>
                        ))}
                    <BaseDropdown
                        className='mt-4'
                        control={control}
                        placeholder='Country'
                        name='country_id'
                        clearErrors={clearErrors}
                        errors={[]}
                        options={countries}
                    />
                    <BaseTextarea
                        className='mt-4'
                        type='text'
                        control={control}
                        placeholder='Description'
                        name='description'
                        clearErrors={clearErrors}
                        errors={[...(errors?.description ? errors.description : [])]}
                    />
                    <BaseSwitch
                        className='mt-4'
                        control={control}
                        label='All day event'
                        name='all_day'
                        labelClassName='cursor-pointer'
                    />
                    {(errors?.start_time || errors?.end_time) && (
                        <div>
                            {errors?.start_time &&
                                errors?.start_time.map((error) => {
                                    return (
                                        <div key={error} className='mt-2.5 text-theme1-error'>
                                            {error}
                                        </div>
                                    );
                                })}
                            {errors?.end_time &&
                                errors?.end_time.map((error) => {
                                    return (
                                        <div key={error} className='mt-2.5 text-theme1-error'>
                                            {error}
                                        </div>
                                    );
                                })}
                        </div>
                    )}
                    <div className='mt-4'>
                        <div className='flex items-center justify-between'>
                            <div className='text-theme1-primary2'>Starts:</div>
                            <div className='col-span-2 font-bold'>
                                {formValues.start_date ? dayjs(formValues.start_date).format('MMM DD, YYYY') : ''}
                            </div>
                            {!allDay && (
                                <div className='w-20'>
                                    <BaseInputCalendar
                                        name='start_time'
                                        control={control}
                                        timeOnly
                                        placeholder='Time'
                                        extraOnChange={(value) => checkAndModifyEndTime(value)}
                                    />
                                </div>
                            )}
                        </div>
                        <div className='flex items-center justify-between mt-2'>
                            <div className='text-theme1-primary2'>Ends:</div>
                            <div className='col-span-2 font-bold'>
                                {formValues.end_date ? dayjs(formValues.end_date).format('MMM DD, YYYY') : ''}
                            </div>
                            {!allDay && (
                                <div className='w-20'>
                                    <BaseInputCalendar
                                        name='end_time'
                                        control={control}
                                        timeOnly
                                        placeholder='Time'
                                        minDate={
                                            dayjs(formValues.start_date).format('YYYY-MM-DD') ===
                                                dayjs(formValues.end_date).format('YYYY-MM-DD') && startTime
                                                ? new Date(startTime)
                                                : null
                                        }
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <RecurringMeeting
                meeting={calendarMeeting}
                setValue={setValue}
                formState={formState}
                watch={watch}
                clearErrors={clearErrors}
                control={control}
                getValues={getValues}
                reset={reset}
            />

            {!!userPermissions[PERMISSION_CONTACTS]?.read && (
                <MeetingParticipants
                    addNew={fieldsParticipants.length === 0}
                    fields={fieldsParticipants}
                    append={appendParticipant}
                    control={control}
                    clearErrors={clearErrors}
                    remove={removeParticipant}
                    showSearch={showParticipantSearch}
                    setShowSearch={setShowParticipantSearch}
                    canAdd={userPermissions[PERMISSION_CONTACTS]?.create}
                />
            )}
            {!!userPermissions[PERMISSION_OWNED_ORGANIZATIONS]?.read &&
                userPermissions[PERMISSION_PARTNER_ORGANIZATIONS]?.read && (
                    <AssociateOrganizations
                        fields={fieldsOrganizations}
                        append={appendOrganization}
                        control={control}
                        clearErrors={clearErrors}
                        remove={removeOrganization}
                        showSearch={showOrganizationSearch}
                        setShowSearch={setShowOrganizationSearch}
                    />
                )}
            {!!userPermissions[PERMISSION_PROJECTS]?.read && (
                <AssociateProjects
                    fields={fieldsProjects}
                    append={appendProject}
                    control={control}
                    clearErrors={clearErrors}
                    remove={removeProject}
                    showSearch={showProjectSearch}
                    setShowSearch={setShowProjectSearch}
                />
            )}

            {!!userPermissions[PERMISSION_FILES]?.read && (
                <AssociateFiles
                    fields={fieldsFiles}
                    append={appendFile}
                    remove={removeFile}
                    showSearch={showFileSearch}
                    setShowSearch={setShowFileSearch}
                />
            )}

            <Notes
                fields={fieldsNotes}
                control={control}
                append={appendNote}
                remove={removeNote}
                clearErrors={clearErrors}
            />

            <div className='max-w-3xl w-full mx-auto px-8'>
                <div className='mb-6'>
                    <div className='grid grid-cols-2 gap-4'>
                        {!edit || !calendarMeeting?.is_recurrent ? (
                            <BaseButton
                                id='btn-submit'
                                className='btn-primary'
                                onClick={edit ? handleSubmit(sendEditMeeting) : handleSubmit(sendAddMeeting)}
                                isLoading={edit ? isLoadingEdit : isLoading}
                                textValue='Save'
                                disabled={!formValues.title || !formValues.start_date || !formValues.end_date}
                            />
                        ) : (
                            <BaseButton
                                id='btn-submit-recurring'
                                className='btn-primary'
                                onClick={handleSaveRecurring}
                                textValue={'Save'}
                                disabled={!formValues.title || !formValues.start_date || !formValues.end_date}
                            />
                        )}

                        <BaseButton
                            id='btn-cancel'
                            className='btn-primary-outline'
                            onClick={() => navigate('/calendar')}
                            textValue='Cancel'
                        />
                    </div>
                </div>
            </div>

            {!!userPermissions[PERMISSION_CALENDAR]?.delete &&
                edit &&
                calendarMeeting &&
                canUpdateOrDelete(user, calendarMeeting, PERMISSION_CALENDAR, 'delete') && (
                    <div className='flex flex-col max-w-3xl w-full mx-auto px-8 mb-6'>
                        <div>
                            <div className='flex flex-col justify-center items-center'>
                                <BaseButton
                                    className='btn-red'
                                    onClick={handleDeleteConfirmation}
                                    textValue={'Delete'}
                                />
                            </div>
                        </div>
                    </div>
                )}

            {deleteVisible && (
                <MeetingDeleteConfirmation
                    deleteVisible={deleteVisible}
                    setDeleteVisible={setDeleteVisible}
                    meeting={calendarMeeting}
                />
            )}

            {saveRecurringVisible && (
                <Dialog
                    className='rounded-4xl'
                    visible={saveRecurringVisible}
                    style={{ width: '400px' }}
                    breakpoints={{ '420px': '320px' }}
                    onHide={() => setSaveRecurringVisible(false)}
                    content={() => (
                        <div className='flex flex-col justify-center items-center px-6 py-8 pt-12 bg-white rounded-4xl'>
                            <button onClick={() => setSaveRecurringVisible(false)} className='absolute top-6 right-6'>
                                <IconCloseFull className='fill-theme1-text5' />
                            </button>
                            <div className='text-theme1-primary text-[22px] mb-[30px] text-center leading-tight mt-4'>
                                <p>Save changes for...</p>
                            </div>
                            <div className='w-full'>
                                <BaseButton
                                    className='btn-primary mx-auto'
                                    onClick={handleSubmit(sendEditMeeting)}
                                    textValue={'This meeting only'}
                                    disabled={isLoadingEdit || calendarMeeting.is_original}
                                />
                                <BaseButton
                                    className='btn-primary mx-auto mt-6'
                                    onClick={() => saveToAllOccurrences()}
                                    textValue={'All meetings in the series'}
                                    disabled={isLoadingEdit}
                                />
                                <BaseButton
                                    className='btn-red-outline mx-auto mt-6'
                                    textValue='Cancel'
                                    onClick={() => setSaveRecurringVisible(false)}
                                />
                            </div>
                        </div>
                    )}
                ></Dialog>
            )}
        </AuthLayout>
    );
};

export default AddEditMeeting;
